/**
 * Workspace / personal writing goals stored in workspace metadata and
 * device-local progress maps.
 */

export const GOAL_WORD_COUNT_KEY = 'goalWordCount';
export const GOAL_DEADLINE_KEY = 'goalDeadline';
export const WORDS_PER_DAY_GOAL_KEY = 'wordsPerDayGoal';

export type WorkspaceGoalMetadata = {
  goalWordCount: number | null;
  goalDeadline: string | null;
  wordsPerDayGoal: number | null;
};

export function readWorkspaceGoals(
  metadata: Record<string, unknown> | undefined,
): WorkspaceGoalMetadata {
  return {
    goalWordCount: positiveIntOrNull(metadata?.[GOAL_WORD_COUNT_KEY]),
    goalDeadline: isoDateOrNull(metadata?.[GOAL_DEADLINE_KEY]),
    wordsPerDayGoal: positiveIntOrNull(metadata?.[WORDS_PER_DAY_GOAL_KEY]),
  };
}

export function positiveIntOrNull(value: unknown): number | null {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseInt(value, 10)
        : Number.NaN;
  if (!Number.isFinite(parsed)) {
    return null;
  }
  const rounded = Math.round(parsed);
  return rounded > 0 ? rounded : null;
}

/** Accepts YYYY-MM-DD only. */
export function isoDateOrNull(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return null;
  }
  const parsed = new Date(`${trimmed}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return trimmed;
}

export function daysRemaining(deadline: string, now = new Date()): number {
  const end = new Date(`${deadline}T23:59:59.999Z`).getTime();
  const diff = end - now.getTime();
  return Math.ceil(diff / (24 * 60 * 60 * 1000));
}

export function todayIsoDate(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export type DailyWordProgress = {
  date: string;
  /** Workspace word total at the start of the day (baseline). */
  baselineWords: number;
  /** Words written today = max(0, currentWords - baselineWords). */
  wordsWritten: number;
};

export function progressForToday(params: {
  stored: DailyWordProgress | null | undefined;
  currentWords: number;
  today?: string;
}): DailyWordProgress {
  const today = params.today ?? todayIsoDate();
  const currentWords = Math.max(0, Math.round(params.currentWords));
  if (!params.stored || params.stored.date !== today) {
    return {
      date: today,
      baselineWords: currentWords,
      wordsWritten: 0,
    };
  }
  return {
    date: today,
    baselineWords: params.stored.baselineWords,
    wordsWritten: Math.max(0, currentWords - params.stored.baselineWords),
  };
}
